import React, { useCallback, useState, FC, useMemo } from 'react';
import { Checkbox, ConditionalWrapper, Dropdown, Form, Spinner, Tooltip } from 'src/common';
import styles from './index.module.scss';
import { faCaretDown } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import { useApiTenders } from 'src/models/procurements/Tenders/hooks';
import { TendersSearchParams } from 'src/models/procurements/Tenders/types';
import { debounce } from 'lodash';
import { FeatureFlag, useFeatureFlag } from 'src/helpers';
import { useForm } from 'antd/lib/form/Form';
import { SearchOnlyStatus } from '@tendium/prom-types';
import { trackTendersFilterStatuses } from 'src/segment/events';
import { useTranslation } from 'react-i18next';

interface FormState {
  isHandled?: boolean;
  isExpired?: boolean;
  isAwarded?: boolean;
}

interface Props {
  className?: string;
  disabled?: boolean;
  loading?: boolean;
}

const FilterStatuses: FC<Props> = ({ disabled, className, loading }) => {
  const { t } = useTranslation();
  const [{ vars, initVars, eventSource }, updateSearchArgs] = useApiTenders();
  const [menuVisible, setMenuVisible] = useState(false);
  const onVisibleChange = useCallback(() => {
    setMenuVisible(prevState => !prevState);
  }, []);

  const isDisabled = !!(disabled || loading);
  const isSearchApplied = useMemo(
    () =>
      vars.onlyStatus !== initVars.onlyStatus ||
      vars.awarded !== initVars.awarded ||
      vars.isUnhandled !== initVars.isUnhandled,
    [initVars.awarded, initVars.isUnhandled, initVars.onlyStatus, vars.awarded, vars.isUnhandled, vars.onlyStatus]
  );
  const isHistoryAvailable = useFeatureFlag(FeatureFlag.MatchingProfile_HistoricalTenderFilter);
  const isShowOnlyAwardedFeature = useFeatureFlag(FeatureFlag.ShowOnlyAwarded);

  const [form] = useForm();

  const initialValues: FormState = useMemo(() => {
    return {
      isHandled: vars.isUnhandled !== true,
      isExpired: vars.onlyStatus !== initVars.onlyStatus,
      isAwarded: vars.awarded
    };
  }, [initVars.onlyStatus, vars.awarded, vars.isUnhandled, vars.onlyStatus]);

  const onValuesChange = useMemo(
    () =>
      debounce((changedValues: Partial<FormState>) => {
        if (changedValues.isHandled === true) {
          updateSearchArgs({ [TendersSearchParams.isUnhandled]: false });
        } else if (changedValues.isHandled === false) {
          updateSearchArgs({ [TendersSearchParams.isUnhandled]: true });
        } else if (changedValues.isExpired === false) {
          updateSearchArgs({
            [TendersSearchParams.onlyStatus]: initVars.onlyStatus
          });
        } else if (changedValues.isExpired === true) {
          updateSearchArgs({
            [TendersSearchParams.onlyStatus]: SearchOnlyStatus.Expired
          });
        } else {
          updateSearchArgs({
            [TendersSearchParams.awarded]: changedValues.isAwarded ? changedValues.isAwarded : undefined
          });
        }

        /** @todo check why not just original values? */
        /* trackTendersFilterStatuses(eventSource, changedValues); */
        trackTendersFilterStatuses(eventSource, {
          isExpired: changedValues.isExpired ?? vars.onlyStatus === SearchOnlyStatus.Expired,
          isAwarded: changedValues.isAwarded ?? !!vars.awarded
        });
      }, 300),
    [eventSource, initVars.onlyStatus, updateSearchArgs, vars.awarded, vars.onlyStatus]
  );

  return (
    <div className={classNames(styles.container, className)}>
      <Dropdown
        trigger={['click']}
        onVisibleChange={onVisibleChange}
        visible={menuVisible}
        placement={'bottomRight'}
        disabled={isDisabled}
        overlay={
          <div className={styles.overlay}>
            <Form form={form} className={styles.form} onValuesChange={onValuesChange} initialValues={initialValues}>
              {eventSource === 'Mp' && (
                <Form.Item name={'isHandled'} valuePropName={'checked'}>
                  <Checkbox>
                    <span>{t('Tenders.showHandledTenders')}</span>
                  </Checkbox>
                </Form.Item>
              )}
              <ConditionalWrapper
                condition={!isHistoryAvailable}
                wrapper={children => <Tooltip title={t('Tenders.expiredTendersTooltip')}>{children}</Tooltip>}
              >
                <Form.Item name={'isExpired'} valuePropName={'checked'}>
                  <Checkbox disabled={!isHistoryAvailable}>
                    <span>{t('Tenders.showExpiredTenders')}</span>
                  </Checkbox>
                </Form.Item>
              </ConditionalWrapper>
              {isShowOnlyAwardedFeature && (
                <Form.Item name={'isAwarded'} valuePropName={'checked'}>
                  <Checkbox>
                    <span>{t('Tenders.showOnlyAwardedTenders')}</span>
                  </Checkbox>
                </Form.Item>
              )}
            </Form>
          </div>
        }
      >
        <button
          className={classNames(styles.button, { [styles.isActiveButton]: isSearchApplied })}
          disabled={isDisabled}
        >
          <span>{t('Common.status')}</span>
          {!loading ? (
            <FontAwesomeIcon
              icon={faCaretDown}
              className={classNames(styles.buttonIcon, { [styles.isActive]: menuVisible })}
            />
          ) : (
            <Spinner className={classNames(styles.buttonIcon, styles.spinner)} />
          )}
        </button>
      </Dropdown>
    </div>
  );
};

export default FilterStatuses;
